export type OverlayScrollAxis = 'y' | 'x';

export interface PointerCoords {
    clientX: number;
    clientY: number;
}

export interface ScrollMetrics {
    offset: number;
    size: number;
    viewport: number;
}

export interface OverlayScrollAxisAdapter {
    readMetrics: (element: HTMLElement) => ScrollMetrics;
    scrollTo: (element: HTMLElement, offset: number) => void;
    alongAxis: (pointer: PointerCoords) => number;
    acrossAxis: (pointer: PointerCoords) => number;
    // The region edge the thumb rides along: right for a vertical bar, bottom for a horizontal one.
    thumbEdge: (regionRect: DOMRect) => number;
    paintThumb: (thumb: HTMLElement, length: number, offset: number) => void;
}

export const OVERLAY_SCROLL_AXES: Readonly<Record<OverlayScrollAxis, OverlayScrollAxisAdapter>> = {
    y: {
        readMetrics: (element) => ({
            offset: element.scrollTop,
            size: element.scrollHeight,
            viewport: element.clientHeight
        }),
        scrollTo: (element, offset) => {
            element.scrollTop = offset;
        },
        alongAxis: (pointer) => pointer.clientY,
        acrossAxis: (pointer) => pointer.clientX,
        thumbEdge: (regionRect) => regionRect.right,
        paintThumb: (thumb, length, offset) => {
            thumb.style.height = `${length}px`;
            thumb.style.transform = `translateY(${offset}px)`;
        }
    },
    x: {
        readMetrics: (element) => ({
            offset: element.scrollLeft,
            size: element.scrollWidth,
            viewport: element.clientWidth
        }),
        scrollTo: (element, offset) => {
            element.scrollLeft = offset;
        },
        alongAxis: (pointer) => pointer.clientX,
        acrossAxis: (pointer) => pointer.clientY,
        thumbEdge: (regionRect) => regionRect.bottom,
        paintThumb: (thumb, length, offset) => {
            thumb.style.width = `${length}px`;
            thumb.style.transform = `translateX(${offset}px)`;
        }
    }
};

export interface ThumbLayout {
    length: number;
    offset: number;
    maxScroll: number;
    maxThumbOffset: number;
}

// Proportional to the visible fraction, never an ungrabbable sliver, never longer than its track.
export function computeThumbLayout(
    metrics: ScrollMetrics,
    trackInsetPx: number,
    minThumbLengthPx: number,
    overflowEpsilonPx: number
): ThumbLayout | null {
    const maxScroll = metrics.size - metrics.viewport;
    const trackLength = metrics.viewport - trackInsetPx * 2;
    if (maxScroll <= overflowEpsilonPx || trackLength <= 0) {
        return null;
    }
    const length = Math.min(
        trackLength,
        Math.max(minThumbLengthPx, (metrics.viewport / metrics.size) * trackLength)
    );
    const maxThumbOffset = trackLength - length;
    const offset = trackInsetPx + (metrics.offset / maxScroll) * maxThumbOffset;
    return { length, offset, maxScroll, maxThumbOffset };
}
